import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { COLUMN_ACTION_GROUP } from '../../constants';
import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { LlmColumnModel } from '../llm-column.model';
import { toast, CollapsibleError } from '@cotera/client/app/components/ui';
import { Assert } from '@cotera/utilities';
import { GraphModel } from '@cotera/client/v0/graph/graph.model';
import { DG } from '@cotera/sdk/core';
import { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import React from 'react';

export class ConvertToV2Action extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  title = 'Convert to V2';
  inputSchema = z.object({});
  group = COLUMN_ACTION_GROUP;
  icon = 'arrow-right';

  constructor(
    private readonly queryClient: QueryClient,
    private readonly toolsV2Client: TenantedClient['toolsV2'],
    private readonly orgId: string
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    if (!context.isInScope(GraphModel)) {
      return false;
    }

    if (!context.hasTargetedModelOfType(LlmColumnModel)) {
      return false;
    }

    const model = context.targetedModelOfType(LlmColumnModel);
    if (model === null) {
      return false;
    }

    // Only applicable for v1 configs
    const config = model.config.snapshot();
    if (config === null) {
      return false;
    }

    if (config.version === undefined) {
      return true;
    }

    return config.version === 'v1';
  }

  async execute(
    _: {},
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(LlmColumnModel);
    const graph = context.getInScopeModelOfType(GraphModel);

    Assert.assert(model !== null);
    Assert.assert(graph !== null);

    const config = model.config.snapshot();
    if (config === null) {
      return err([{ t: 'error', message: 'Column config not found' }]);
    }

    // Ensure it's a v1 config
    if (config.version === 'v2') {
      return err([{ t: 'error', message: 'Column is already v2' }]);
    }

    const v1Config = config as DG.Graph.LlmColConfig;

    // Fetch tools from toolsV2 API to get implementation keys
    const tools = await this.queryClient.fetchQuery<
      {
        key: string;
        implementationKey: string;
        name: string;
      }[]
    >({
      queryKey: queryKeys.toolsV2.all({ orgId: this.orgId }),
      queryFn: async () => {
        return Assert.assertOk(await this.toolsV2Client.list({}));
      },
    });

    // Build a map from legacy tool UUID to implementation key
    // tool.key is the database ID (UUID) for legacy tools
    // tool.implementationKey is the new tool key (e.g., "google-workspace/gmail/listMessages")
    const toolsMap = new Map<string, { id: string; label: string }>();
    for (const tool of tools) {
      // Use implementation key as the id for EditorState.fromV1
      toolsMap.set(tool.key, { id: tool.implementationKey, label: tool.name });
    }

    // Capture snapshots to ensure consistency
    const currentNameInGraph = model.currentNameInGraph.snapshot();
    const graphSnapshot = graph.graph.snapshot();

    // Convert editor state from v1 to v2 using implementation keys
    const editorState = DG.DocumentEditor.EditorState.fromV1({
      columnName: currentNameInGraph,
      llmConfig: v1Config,
      tools: v1Config.tools
        .map((toolId) => toolsMap.get(toolId))
        .filter((t): t is { id: string; label: string } => t !== undefined),
    });

    // Create v2 config
    const v2Config: DG.Graph.LlmColConfigV2 = {
      t: 'llm',
      version: 'v2',
      outputTy: v1Config.outputTy,
      editorState: editorState.getState(),
      model: v1Config.model,
      schedule: v1Config.when,
      temperature: v1Config.temperature,
      topP: v1Config.topP,
      topK: v1Config.topK,
      frequencyPenalty: v1Config.frequencyPenalty,
      presencePenalty: v1Config.presencePenalty,
      maxOutputTokens: v1Config.maxOutputTokens,
    };

    // Get the column from the graph
    const columnBuildExplorer = graphSnapshot.getColumn({
      rel: model.datasetName,
      name: currentNameInGraph,
    });

    if (columnBuildExplorer === null) {
      return err([{ t: 'error', message: 'Column not found in graph' }]);
    }

    const ops = columnBuildExplorer.makeUpdateOps({
      config: v2Config,
    });

    if (ops.isErr()) {
      return err([{ t: 'error', message: JSON.stringify(ops.error) }]);
    }

    // Compile to verify the conversion works
    // Build compiler from graph commits to ensure consistency
    const compilerSnapshot = graphSnapshot.compiler;

    const tempCommit: DG.Commit = {
      actorId: 'system',
      timestamp: new Date(),
      operations: ops.value,
      message: null,
    };

    const compilationResult = compilerSnapshot.commit(tempCommit).compile();

    if (compilationResult.isErr()) {
      const errorMessage = compilationResult.error.prettyPrint();
      toast.error('Failed to compile graph after conversion', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([{ t: 'error', message: errorMessage }]);
    }

    const compiledSymbolResult =
      compilationResult.value.graph[model.datasetName]!;

    if (compiledSymbolResult.isErr()) {
      const errorMessage = compiledSymbolResult.error.prettyPrint();
      toast.error('Failed to compile column after conversion', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([{ t: 'error', message: errorMessage }]);
    }

    model.v2EditorState.set(editorState.getState());

    // Stage the operations
    const stagingResult = await graph.attemptToStageOperations(ops.value);

    if (stagingResult.isErr()) {
      const errorMessage = stagingResult.error.prettyPrint();
      toast.error('Failed to stage operations', {
        description: <CollapsibleError error={errorMessage} />,
      });
      return err([{ t: 'error', message: errorMessage }]);
    }

    toast.success('Column converted to v2 format');

    return ok({});
  }
}
